import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ArrowLeft,
  Workflow,
  FileText,
  BarChart3,
  X, 
  ShieldCheck,

  // --- Iconos NUEVOS para esta página ---
  BrainCircuit,   // Para IA (Hero)
  UploadCloud,    // Para IA de Terceros
  Server,         // Para IA Local / In-House
  Lock,           // Para Seguridad de Datos
  Settings2,      // Para Personalización
  Code,           // Para Python / Desarrollo
  ArrowRight,     // Para diagramas de flujo
  FileSearch,     // ¡NUEVO! Para caso de uso 1
  Cpu             // ¡NUEVO! Para Hardware

} from 'lucide-react';

import ContactForm from '../ContactForm';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ParticlesBackground from '../components/ParticlesBackground';


// --- COMPONENTES DE PÁGINA (Reutilizados) ---

// Header
const PageHeader = ({ onOpenPopup, ctaText }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {ctaText}
        </button>
      </div>
    </div>
  </header>
);

// --- PÁGINA DE IA LOCAL ---

const IA_Local_Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Estado para controlar el popup
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Función para abrir el popup
  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Función para cerrar el popup
  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const capabilities = useMemo(() => t('ia_local.capabilities.items', { returnObjects: true }), [t]);
  const hardwareItems = useMemo(() => t('ia_local.hardware.items', { returnObjects: true }), [t]);

  // Componente reutilizable para tarjetas de características (Reutilizado)
  const FeatureCard = ({ icon: Icon, title, children, colorClass, bgColorClass = "bg-gray-50", borderColorClass = "border-gray-200" }) => (
    <div className={`${bgColorClass} ${borderColorClass} border rounded-lg p-6 h-full transition hover:shadow-md flex flex-col`}>
      <div className="flex items-center gap-3 mb-3 min-h-[56px]"> {/* <-- CORRECCIÓN: Altura mínima para alinear títulos */}
        <Icon className={colorClass} size={24} />
        <h4 className="font-semibold text-lg text-[#141414]">{title}</h4>
      </div>
      {/* El 'children' ahora contendrá el visual y el texto */}
      <div className="text-sm text-gray-700 flex-grow">{children}</div>
    </div>
  );
  
  // Componente para item de hardware (NUEVO DISEÑO)
  const HardwareItem = ({ icon: Icon, title, text }) => (
    <div className="flex items-start gap-4">
      <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-gray-200 text-gray-700 rounded-lg">
        <Icon size={20} />
      </span>
      <div>
        <h5 className="font-semibold text-gray-800">{title}</h5>
        <p className="text-sm text-gray-600">{text}</p>
      </div>
    </div>
  );


  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />
      {/* Popup Modal para Bitrix24 */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label="Cerrar popup"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('ia_local.final.btn')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}
      
      <PageHeader onOpenPopup={openPopup} ctaText={t('common.free_diagnostic')} />

      {/* Contenido Principal */}
      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Encabezado Principal (Hero Section) */}
          <div className="text-center mb-24">
            <div className="inline-block bg-blue-100 text-blue-700 p-3 rounded-full mb-4">
              <BrainCircuit size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('ia_local.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              {t('ia_local.hero.subtitle')}
            </p>
            <p className="text-base text-gray-500 max-w-4xl mx-auto">
              {t('ia_local.hero.desc')}
            </p>
          </div>
          
          
          {/* --- INICIO NUEVO DISEÑO --- */}

          {/* Sección 1: Entendiendo los Modelos (Visual) - CORREGIDO */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('ia_local.deployment.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
              {t('ia_local.deployment.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              
              {/* Modelo 1: IA de Terceros (API) */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-700 rounded-full">
                    <UploadCloud size={20} />
                  </span>
                  <h3 className="text-xl font-semibold text-gray-800">{t('ia_local.deployment.third_party.title')}</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">{t('ia_local.deployment.third_party.desc')}</p>
                {/* Diagrama */}
                <div className="flex-grow flex items-center justify-center bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                  <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-4">
                    <div className="text-center">
                      {/* CORRECCIÓN: Tamaño aumentado */}
                      <div className="bg-white shadow-md rounded-lg p-5 border border-gray-200 min-w-[140px]">
                        <Lock size={20} className="mx-auto text-gray-700" />
                        <p className="font-bold text-gray-800 text-sm mt-1">{t('ia_local.deployment.third_party.labels.data')}</p>
                      </div>
                    </div>
                    <ArrowRight size={24} className="text-gray-500 rotate-90 md:rotate-0" />
                    <div className="text-center">
                      {/* CORRECCIÓN: Tamaño aumentado */}
                      <div className="bg-white shadow-md rounded-lg p-5 border border-gray-200 min-w-[140px]">
                        <UploadCloud size={20} className="mx-auto text-blue-700" />
                        <p className="font-bold text-blue-700 text-sm mt-1">{t('ia_local.deployment.third_party.labels.cloud')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modelo 2: IA Local (Desarrollo Propio) */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full">
                    <Server size={20} />
                  </span>
                  <h3 className="text-xl font-semibold text-blue-700">{t('ia_local.deployment.local.title')}</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">{t('ia_local.deployment.local.desc')}</p>
                {/* Diagrama */}
                <div className="flex-grow flex items-center justify-center bg-blue-50 border border-blue-200 rounded-lg p-6 mt-2">
                  <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200 text-center w-full">
                    <div className="inline-block p-3 bg-gray-100 rounded-full mb-3">
                      <Server size={24} className="text-gray-800" />
                    </div>
                    <h5 className="font-bold text-gray-800">{t('ia_local.deployment.local.labels.server')}</h5>
                    <div className="mt-4 flex items-center justify-center space-x-4">
                      {/* CORRECCIÓN: Tamaño aumentado */}
                      <div className="text-center p-4 rounded-lg bg-gray-50 min-w-[110px]">
                        <Lock size={20} className="mx-auto text-gray-700" />
                        <p className="font-semibold text-gray-800 text-sm mt-1">{t('ia_local.deployment.local.labels.data')}</p>
                      </div>
                      <span className="text-2xl font-light text-gray-400">+</span>
                      {/* CORRECCIÓN: Tamaño aumentado */}
                      <div className="text-center p-4 rounded-lg bg-gray-50 min-w-[110px]">
                        <BrainCircuit size={20} className="mx-auto text-blue-700" />
                        <p className="font-semibold text-blue-700 text-sm mt-1">{t('ia_local.deployment.local.labels.ia')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Sección 2: Capacidades Estratégicas (Antes "El Poder de un LLM") */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('ia_local.capabilities.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
              {t('ia_local.capabilities.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {capabilities.map((item, index) => (
                <FeatureCard
                  key={index}
                  icon={[FileText, Settings2, BarChart3, Workflow][index]}
                  colorClass="text-blue-600"
                  title={item.title}
                  bgColorClass="bg-blue-50"
                  borderColorClass="border-blue-200"
                >
                  <p>{item.desc}</p>
                </FeatureCard>
              ))}
            </div>
          </section>

          {/* ¡NUEVA SECCIÓN! 3: Casos de Uso Avanzados en Acción (NUEVO DISEÑO ZIG-ZAG) */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('ia_local.cases.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              {t('ia_local.cases.subtitle')}
            </p>
            
            {/* Contenedor del Zig-Zag */}
            <div className="space-y-16">

              {/* Caso de Uso 1: Búsqueda Inteligente */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Visual (AHORA A LA DERECHA) */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 shadow-md md:order-last">
                  <div className="flex items-center gap-3 mb-3">
                    <FileSearch className="text-indigo-600" size={24} />
                    <h4 className="font-semibold text-lg text-[#141414]">{t('ia_local.cases.case1.mockup')}</h4>
                  </div>
                  <div className="bg-white border border-gray-200 rounded p-4 text-sm font-mono shadow-sm">
                    <span className="text-gray-400">&gt; </span>
                    <span className="text-indigo-700">{t('ia_local.cases.case1.query')}</span>
                  </div>
                </div>
                {/* Texto (AHORA A LA IZQUIERDA CON VIÑETAS) */}
                <div>
                  <h3 className="text-2xl font-semibold text-[#141414] mb-3">{t('ia_local.cases.case1.title')}</h3>
                  <ul className="list-disc list-outside space-y-2 pl-5 text-gray-700">
                    {t('ia_local.cases.case1.points', { returnObjects: true }).map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Caso de Uso 2: Asistente de BI */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Texto (AHORA A LA IZQUIERDA CON VIÑETAS) */}
                <div className="md:order-first"> {/* Se quita md:order-last */}
                  <h3 className="text-2xl font-semibold text-[#141414] mb-3">{t('ia_local.cases.case2.title')}</h3>
                  <ul className="list-disc list-outside space-y-2 pl-5 text-gray-700">
                    {t('ia_local.cases.case2.points', { returnObjects: true }).map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
                {/* Visual (AHORA A LA DERECHA) */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 shadow-md md:order-last">
                   <div className="flex items-center gap-3 mb-3">
                    <BarChart3 className="text-indigo-600" size={24} />
                    <h4 className="font-semibold text-lg text-[#141414]">{t('ia_local.cases.case2.mockup')}</h4>
                  </div>
                  <div className="bg-white border border-gray-200 rounded p-4 shadow-sm flex items-end h-[80px] space-x-3">
                    <div className="w-1/4 bg-indigo-300 h-full rounded-t-sm" style={{height: '80%'}} title="Q1 Región A"></div>
                    <div className="w-1/4 bg-indigo-300 h-full rounded-t-sm" style={{height: '50%'}} title="Q1 Región B"></div>
                    <div className="w-1/4 bg-indigo-500 h-full rounded-t-sm" style={{height: '70%'}} title="Q2 Región A"></div>
                    <div className="w-1/4 bg-indigo-500 h-full rounded-t-sm" style={{height: '90%'}} title="Q2 Región B"></div>
                  </div>
                </div>
              </div>

              {/* Caso de Uso 3: Auditoría en Tiempo Real */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Visual (AHORA A LA DERECHA) */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 shadow-md md:order-last">
                   <div className="flex items-center gap-3 mb-3">
                    <ShieldCheck className="text-indigo-600" size={24} />
                    <h4 className="font-semibold text-lg text-[#141414]">{t('ia_local.cases.case3.mockup')}</h4>
                  </div>
                  <div className="bg-gray-900 text-white border border-gray-700 rounded p-4 text-xs font-mono shadow-sm">
                    <p><span className="text-red-400">{t('ia_local.cases.case3.alert.tag')}</span> <span className="text-gray-300">{t('ia_local.cases.case3.alert.id')}</span></p>
                    <p><span className="text-yellow-400">{t('ia_local.cases.case3.alert.info_label')}</span> <span className="text-gray-300">{t('ia_local.cases.case3.alert.info_text')}</span></p>
                    <p><span className="text-yellow-400">{t('ia_local.cases.case3.alert.time_label')}</span> <span className="text-gray-300">{t('ia_local.cases.case3.alert.time_text')}</span></p>
                    <p><span className="text-yellow-400">{t('ia_local.cases.case3.alert.action_label')}</span> <span className="text-gray-300">{t('ia_local.cases.case3.alert.action_text')}</span></p>
                  </div>
                </div>
                {/* Texto (AHORA A LA IZQUIERDA CON VIÑETAS) */}
                <div>
                  <h3 className="text-2xl font-semibold text-[#141414] mb-3">{t('ia_local.cases.case3.title')}</h3>
                  <ul className="list-disc list-outside space-y-2 pl-5 text-gray-700">
                    {t('ia_local.cases.case3.points', { returnObjects: true }).map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </section>


          {/* Sección 4: Consideraciones de Hardware (CORREGIDO) */}
          <section className="mb-20">
             <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('ia_local.hardware.title')}</h2>
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Columna de Texto */}
              <div>
                <h3 className="text-2xl font-semibold text-[#141414] mb-4">{t('ia_local.hardware.investment_title')}</h3>
                <p className="text-gray-700 mb-4">
                  {t('ia_local.hardware.investment_desc1')}
                </p>
                <p className="text-gray-700">
                  {t('ia_local.hardware.investment_desc2')}
                </p>
              </div>
              
              {/* Columna de Tarjeta Resumen (NUEVO DISEÑO) */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-5">
                {hardwareItems.map((item, index) => (
                  <HardwareItem
                    key={index}
                    icon={[Server, Cpu, Code][index]}
                    title={item.title}
                    text={item.desc}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* --- FIN NUEVO DISEÑO --- */}

        </div>

        {/* Sección 5: Cierre y Conversión */}
        <section className="max-w-4xl mx-auto mt-24 mb-12">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">
              {t('ia_local.final.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              {t('ia_local.final.desc')}
            </p>
            {/* Botón para abrir el popup */}
            <button
              onClick={openPopup}
              className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold"
            >
              <BrainCircuit size={18} />
              {t('ia_local.final.btn')}
            </button>
             <p className="text-sm text-gray-500 max-w-xl mx-auto mt-4">
              {t('ia_local.final.note')}
            </p>
          </div>
        </section>

      </main>
    </div>
  );
};

export default IA_Local_Page;

